import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import api from '../services/api';
import { useAuth } from './AuthContext';

// Browser calling — one Twilio Voice Device per session, one call at a
// time, shared by every "call via Gain Rhino VoIP" icon in the app.
// Design: docs/plans/2026-09-21-browser-calling-design.md
//
// The SDK is imported lazily on first use so reps who never call (or
// orgs without the add-on) don't pay for the bundle.

const BrowserCallContext = createContext(null);

const IDLE = { status: 'idle', lead: null, phone: '', startedAt: null, endedAt: null, error: null, muted: false, callSid: null };

// Query keys whose data changes once a call is logged server-side.
const AFFECTED_QUERIES = ['lead', 'leads', 'notes', 'activities', 'conversations', 'conversation', 'browser-call-eligibility'];

// getUserMedia failures come in several flavours; name the real one so
// the rep (and support) can act on it instead of a generic "denied".
function micErrorMessage(e) {
  const name = e?.name || '';
  const insecure = typeof window !== 'undefined' && !window.isSecureContext;
  if (insecure) return 'Browser calling needs a secure page (https or localhost). Open the CRM over https and try again.';
  switch (name) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
      return 'Microphone access was blocked. Click the lock icon in the address bar, allow the microphone, then try again. On Mac also check System Settings → Privacy & Security → Microphone for your browser.';
    case 'NotFoundError':
    case 'DevicesNotFoundError':
      return 'No microphone was found. Plug in or select a microphone and try again.';
    case 'NotReadableError':
    case 'TrackStartError':
      return 'The microphone is in use by another app (or the OS blocked it). Close other call apps and try again.';
    case 'OverconstrainedError':
      return 'The selected microphone is unavailable. Pick another input device in your browser settings.';
    default:
      return `Could not access the microphone${name ? ` (${name})` : ''}. Allow the microphone for this site or use your desk phone.`;
  }
}

export function leadDisplayName(lead) {
  if (!lead) return '';
  const full = [lead.firstName, lead.lastName].filter(Boolean).join(' ').trim();
  return lead.name || full || lead.company || lead.phone || 'Lead';
}

export function BrowserCallProvider({ children }) {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [state, setState] = useState(IDLE);
  const deviceRef = useRef(null);
  const callRef = useRef(null);
  const timersRef = useRef([]);

  // Eligibility is server-decided (add-on, provider, assigned+verified
  // line, quota, Twilio configured). Cached a minute; refreshed after
  // every call so the quota badge stays honest.
  const eligibilityQuery = useQuery(
    ['browser-call-eligibility', user?._id],
    () => api.get('/browser-call/eligibility').then((r) => r.data),
    { enabled: !!user?._id, staleTime: 60_000, retry: false, refetchOnWindowFocus: false }
  );
  const eligibility = eligibilityQuery.data || null;
  const micSupported = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
  const canCall = !!eligibility?.eligible && micSupported;
  const busy = state.status === 'connecting' || state.status === 'ringing' || state.status === 'active';

  const fetchToken = useCallback(async () => {
    const { data } = await api.post('/browser-call/token');
    return data.token;
  }, []);

  const getDevice = useCallback(async () => {
    if (deviceRef.current) return deviceRef.current;
    const { Device } = await import('@twilio/voice-sdk');
    const token = await fetchToken();
    const device = new Device(token, {
      codecPreferences: ['opus', 'pcmu'],
      closeProtection: 'A call is in progress. Leave this page and hang up?',
      logLevel: 'error',
    });
    device.on('tokenWillExpire', async () => {
      try { device.updateToken(await fetchToken()); } catch (e) { /* next connect() refetches */ }
    });
    device.on('error', (err) => {
      // Device-level errors (auth, network). Call-level errors are handled on the call.
      console.warn('[browser-call] device error', err);
    });
    deviceRef.current = device;
    return device;
  }, [fetchToken]);

  const clearTimers = () => {
    timersRef.current.forEach((t) => clearTimeout(t));
    timersRef.current = [];
  };

  const invalidateAffected = useCallback(() => {
    AFFECTED_QUERIES.forEach((k) => queryClient.invalidateQueries(k));
  }, [queryClient]);

  const finish = useCallback((next) => {
    callRef.current = null;
    setState((s) => ({ ...s, ...next, endedAt: new Date() }));
    // The call note lands via Twilio's status callback a moment after the
    // browser leg drops; refresh timelines twice to catch it.
    clearTimers();
    timersRef.current.push(setTimeout(invalidateAffected, 1500));
    timersRef.current.push(setTimeout(invalidateAffected, 6000));
    timersRef.current.push(setTimeout(() => setState((s) => (s.status === 'ended' ? IDLE : s)), 4500));
  }, [invalidateAffected]);

  const startCall = useCallback(async ({ lead, phone }) => {
    if (!lead?._id || !phone) return;
    if (busy) { toast.error('Finish the current call first'); return; }
    if (!canCall) { toast.error(eligibility?.message || 'Browser calling is unavailable'); return; }
    clearTimers();
    setState({ ...IDLE, status: 'connecting', lead, phone });
    try {
      // Ask for the mic up-front so a denied permission is a clean error
      // instead of a silent call.
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((t) => t.stop());
    } catch (e) {
      setState((s) => ({ ...s, status: 'error', error: micErrorMessage(e) }));
      return;
    }
    try {
      const device = await getDevice();
      const call = await device.connect({ params: { To: phone, leadId: String(lead._id) } });
      callRef.current = call;
      setState((s) => ({ ...s, callSid: call.parameters?.CallSid || null }));
      call.on('ringing', () => setState((s) => ({ ...s, status: 'ringing' })));
      call.on('accept', () => setState((s) => ({ ...s, status: 'active', startedAt: new Date() })));
      call.on('mute', (isMuted) => setState((s) => ({ ...s, muted: isMuted })));
      call.on('disconnect', () => finish({ status: 'ended' }));
      call.on('cancel', () => finish({ status: 'ended' }));
      call.on('reject', () => finish({ status: 'ended' }));
      call.on('error', (err) => {
        console.warn('[browser-call] call error', err);
        finish({ status: 'error', error: err?.message || 'The call failed.' });
      });
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Could not start the call.';
      finish({ status: 'error', error: msg });
    }
  }, [busy, canCall, eligibility, getDevice, finish]);

  const hangUp = useCallback(() => {
    if (callRef.current) callRef.current.disconnect();
    else if (deviceRef.current) deviceRef.current.disconnectAll();
    else setState(IDLE);
  }, []);

  const toggleMute = useCallback(() => {
    const call = callRef.current;
    if (!call) return;
    call.mute(!call.isMuted());
  }, []);

  const sendDigits = useCallback((digits) => {
    if (callRef.current && digits) callRef.current.sendDigits(String(digits));
  }, []);

  const dismiss = useCallback(() => {
    if (busy) return;
    clearTimers();
    setState(IDLE);
  }, [busy]);

  // Tear the Device down on logout / unmount.
  useEffect(() => () => {
    clearTimers();
    try { deviceRef.current?.destroy(); } catch (_) { /* noop */ }
    deviceRef.current = null;
  }, []);
  useEffect(() => {
    if (!user?._id && deviceRef.current) {
      try { deviceRef.current.destroy(); } catch (_) { /* noop */ }
      deviceRef.current = null;
      setState(IDLE);
    }
  }, [user?._id]);

  const value = useMemo(() => ({
    state, busy, canCall, micSupported, eligibility,
    eligibilityLoading: eligibilityQuery.isLoading,
    refreshEligibility: eligibilityQuery.refetch,
    startCall, hangUp, toggleMute, sendDigits, dismiss,
  }), [state, busy, canCall, micSupported, eligibility, eligibilityQuery.isLoading, eligibilityQuery.refetch, startCall, hangUp, toggleMute, sendDigits, dismiss]);

  return <BrowserCallContext.Provider value={value}>{children}</BrowserCallContext.Provider>;
}

export function useBrowserCall() {
  const ctx = useContext(BrowserCallContext);
  // Components rendered outside the provider (tests, isolated stories)
  // get a safe no-op so they never crash.
  return ctx || {
    state: IDLE, busy: false, canCall: false, micSupported: false, eligibility: null,
    eligibilityLoading: false, refreshEligibility: () => {},
    startCall: () => {}, hangUp: () => {}, toggleMute: () => {}, sendDigits: () => {}, dismiss: () => {},
  };
}

export default BrowserCallContext;
